import { Pt, pt } from "./pt";
import { Matrix } from "./matrix";
import { Bounds } from "./bounds";

interface Segment {
  start: Pt;
  end: Pt;
  bounds: Bounds;

  reverse(): Segment;
  transform(matrix: Matrix): Segment;
  position(t: number): Pt;
  split(t: number, k: Pt): Segment[];
  endpoints(start: Pt, end: Pt): Segment;
}

class LineSegment implements Segment {
  start: Pt;
  end: Pt;

  constructor(start: Pt, end: Pt) {
    this.start = start;
    this.end = end;
  }

  get bounds(): Bounds {
    let xs = [this.start.x, this.end.x];
    let ys = [this.start.y, this.end.y];
    return new Bounds(
      Math.min(...xs),
      Math.min(...ys),
      Math.max(...xs),
      Math.max(...ys)
    );
  }

  position(t: number): Pt {
    return pt(
      this.start.x + (this.end.x - this.start.x) * t,
      this.start.y + (this.end.y - this.start.y) * t
    );
  }

  transform(matrix: Matrix): Segment {
    return new LineSegment(
      matrix.transform(this.start),
      matrix.transform(this.end)
    );
  }

  endpoints(start: Pt, end: Pt): Segment {
    return new LineSegment(start, end);
  }

  reverse(): Segment {
    return new LineSegment(this.end, this.start);
  }

  split(t: number, k: Pt) {
    const left = new LineSegment(this.start, k);
    const right = new LineSegment(k, this.end);
    return [left, right];
  }
}

class CurveSegment implements Segment {
  start: Pt;
  p1: Pt;
  p2: Pt;
  end: Pt;

  constructor(start: Pt, p1: Pt, p2: Pt, end: Pt) {
    this.start = start;
    this.p1 = p1;
    this.p2 = p2;
    this.end = end;
  }

  reverse(): Segment {
    return new CurveSegment(this.end, this.p2, this.p1, this.start);
  }

  endpoints(start: Pt, end: Pt): Segment {
    return new CurveSegment(start, this.p1, this.p2, end);
  }

  transform(matrix: Matrix): Segment {
    return new CurveSegment(
      matrix.transform(this.start),
      matrix.transform(this.p1),
      matrix.transform(this.p2),
      matrix.transform(this.end)
    );
  }

  //TODO
  get bounds(): Bounds {
    let xs = [this.start.x, this.p2.x];
    let ys = [this.start.y, this.p2.y];
    return new Bounds(
      Math.min(...xs),
      Math.min(...ys),
      Math.max(...xs),
      Math.max(...ys)
    );
  }

  //TODO
  position(t: number): Pt {
    return pt(
      this.start.x + (this.end.x - this.start.x) * t,
      this.start.y + (this.end.y - this.start.y) * t
    );
  }

  split(t: number, k: Pt): Segment[] {
    const e = lerp(this.start, this.p1, t);
    const f = lerp(this.p1, this.p2, t);
    const g = lerp(this.p2, this.end, t);
    const h = lerp(e, f, t);
    const j = lerp(f, g, t);

    return [
      new CurveSegment(this.start, e, h, k),
      new CurveSegment(k, j, g, this.end),
    ];
  }
}

function lerp(a: Pt, b: Pt, t: number) {
  return pt(a.x + (b.x - a.x) * t, a.y + (b.y - a.y) * t);
}

export { Segment, LineSegment, CurveSegment };
